import React from "react";
import { StyleSheet, View, type ViewProps, type StyleProp, type ViewStyle } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useThemeColor } from "@/hooks/useThemeColor";
import { BrandColors, Spacing } from "@/constants/ThemeColours";

/**
 * Visual variant presets for ThemedPanel.
 * - `default`: Standard parchment card background with 1px border and tactile shadow.
 * - `accent`: Standard panel with a 4px left highlight accent border (archival folio specimen).
 * - `debossed`: Inset Butter Bean read-only tint for metadata trays, specs, and read-only displays.
 * - `card`: High-density container for list cards and grid items.
 * - `flat`: 1px outline with no drop shadow.
 */
export type ThemedPanelVariant = "default" | "accent" | "debossed" | "card" | "flat";

/**
 * Tactile elevation profiles for Archival Heirloom drop shadows.
 * - `none`: Flat layout with zero shadow.
 * - `low`: Standard subtle elevation for resting cards and in-page sections.
 * - `medium`: Prominent elevation for active panels and highlighted containers.
 * - `high`: Deep elevation for modal dialogs and floating overlays.
 */
export type ThemedPanelElevation = "none" | "low" | "medium" | "high";

/**
 * Props for the ThemedPanel component.
 */
export interface IThemedPanelProps extends ViewProps {
  /** Optional title rendered in the panel header */
  title?: string;
  /** Typographic variant for title (default: "defaultSemiBold") */
  titleType?: "title" | "subtitle" | "default" | "defaultSemiBold" | "editorialTitle" | "stamp";
  /** Optional subtitle or descriptive annotation beneath the title */
  subtitle?: string;
  /** Optional component rendered on the right side of the header (actions, buttons, badges) */
  headerRight?: React.ReactNode;
  /** Custom header component replacing the default title/subtitle header */
  headerComponent?: React.ReactNode;
  /** Visual style variant */
  variant?: "default" | "accent" | "debossed" | "card" | "flat";
  /** Shadow elevation profile (default: "low") */
  elevation?: "none" | "low" | "medium" | "high";
  /** Left accent border color for variant="accent" (default: BrandColors.grassyGreen.hex) */
  accentColor?: string;
  /** Inner content padding (default: 12) */
  padding?: number;
  /** Optional footer component */
  footer?: React.ReactNode;
  /** Custom background color override */
  backgroundColor?: string;
  /** Custom border color override */
  borderColor?: string;
  /** Optional light theme color key override */
  lightColor?: string;
  /** Optional dark theme color key override */
  darkColor?: string;
  /** Style overrides for the root panel container */
  style?: StyleProp<ViewStyle>;
  /** Style overrides for the inner content container */
  contentStyle?: StyleProp<ViewStyle>;
  /** Child elements */
  children?: React.ReactNode;
}

/**
 * ThemedPanel
 *
 * Consistent architectural container for cards, sections, control surfaces, and dialogs.
 * Enforces the Archival Heirloom UX Standard:
 * - 90-degree sharp corners (zero border radius) on all platforms.
 * - Architectural tactile drop shadowing without artificial blur or glow.
 * - Semantic theme colors and responsive padding.
 */
export function ThemedPanel({
  title,
  titleType = "defaultSemiBold",
  subtitle,
  headerRight,
  headerComponent,
  variant = "default",
  elevation = "low",
  accentColor,
  padding = 12,
  footer,
  backgroundColor: customBg,
  borderColor: customBorder,
  lightColor,
  darkColor,
  style,
  contentStyle,
  children,
  ...otherProps
}: IThemedPanelProps) {
  const cardBg = useThemeColor({ light: lightColor, dark: darkColor }, "card");
  const readOnlyBg = useThemeColor({ light: lightColor, dark: darkColor }, "inputReadOnly");
  const defaultBorderColor = useThemeColor({ light: lightColor, dark: darkColor }, "border");
  const textMuted = useThemeColor({}, "textMuted");

  const effectiveBg =
    customBg !== undefined
      ? customBg
      : variant === "debossed"
      ? readOnlyBg
      : cardBg;

  const effectiveBorder = customBorder !== undefined ? customBorder : defaultBorderColor;
  const effectiveAccent = accentColor || BrandColors.grassyGreen.hex;

  // Determine shadow style
  let shadowStyle: ViewStyle = {};
  const effectiveElevation = variant === "flat" ? "none" : elevation;

  if (effectiveElevation === "low") {
    shadowStyle = styles.elevationLow;
  } else if (effectiveElevation === "medium") {
    shadowStyle = styles.elevationMedium;
  } else if (effectiveElevation === "high") {
    shadowStyle = styles.elevationHigh;
  }

  const hasHeader = Boolean(headerComponent || title || subtitle || headerRight);

  return (
    <View
      style={[
        styles.panel,
        {
          backgroundColor: effectiveBg,
          borderColor: effectiveBorder,
        },
        shadowStyle,
        variant === "accent" && {
          borderLeftWidth: 4,
          borderLeftColor: effectiveAccent,
        },
        style,
      ]}
      {...otherProps}
    >
      {/* Custom Header or Standard Title Header */}
      {headerComponent ? (
        headerComponent
      ) : hasHeader ? (
        <View style={[styles.header, { borderBottomColor: effectiveBorder }]}>
          <View style={styles.headerTitleCol}>
            {title ? (
              <ThemedText type={titleType} style={styles.titleText}>
                {title}
              </ThemedText>
            ) : null}
            {subtitle ? (
              <ThemedText style={[styles.subtitleText, { color: textMuted }]}>
                {subtitle}
              </ThemedText>
            ) : null}
          </View>
          {headerRight ? <View style={styles.headerRightCol}>{headerRight}</View> : null}
        </View>
      ) : null}

      {/* Main Panel Content */}
      <View
        style={[
          styles.content,
          { padding },
          hasHeader && styles.contentWithHeader,
          contentStyle,
        ]}
      >
        {children}
      </View>

      {/* Optional Footer */}
      {footer ? (
        <View style={[styles.footer, { borderTopColor: effectiveBorder }]}>
          {footer}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    borderRadius: 0,
    borderWidth: 1,
    overflow: "hidden",
  },
  elevationLow: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  elevationMedium: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  elevationHigh: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: Spacing.two,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: Spacing.two,
  },
  headerTitleCol: {
    flex: 1,
    gap: 2,
  },
  titleText: {
    fontSize: 16,
  },
  subtitleText: {
    fontSize: 12,
    lineHeight: 16,
    opacity: 0.8,
  },
  headerRightCol: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.one,
  },
  content: {
    backgroundColor: "transparent",
  },
  contentWithHeader: {
    // When a header with border bottom exists, padding inside is maintained
  },
  footer: {
    paddingHorizontal: 12,
    paddingVertical: Spacing.two,
    borderTopWidth: StyleSheet.hairlineWidth,
    backgroundColor: "transparent",
  },
});
